import React, { useState } from 'react'
import { Box, Button, Dialog, DialogContent, Grid, TextField, Typography } from '@mui/material'
import { Formik } from 'formik'
import ErrorComponent from '../../shared/components/ui-widgets/ErrorComponent'
import ButtonLoadingComponent from '../../shared/components/ui-widgets/ButtonLoadingComponent'
import { type ForgotPasswordPayload } from '../../../api/schema-dtos/AuthPayloadDtos'
import { useRequestPasswordResetMutation } from '../../../api/user/authenticationApi'
import { setNotification } from '../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../api/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../app/hooks'

interface ForgotPasswordComponentProps {
  title: string
}

const ForgotPasswordComponent: React.FC<ForgotPasswordComponentProps> = ({ title }: ForgotPasswordComponentProps) => {
  const [openDialog, toggleDialog] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const defaultForm: ForgotPasswordPayload = {
    username: ''
  }

  const [apiRequestPasswordReset, requestPasswordResetApiResult] = useRequestPasswordResetMutation()

  const handleRequestPasswordReset = (payload: ForgotPasswordPayload): void => {
    apiRequestPasswordReset(payload)
      .unwrap()
      .then(() => {
        toggleDialog(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  return (
    <>
      <Button
        variant={'outlined'}
        color={'primary'}
        onClick={() => {
          toggleDialog(true)
        }}
      >
        {title}
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'sm'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => {
          toggleDialog(false)
        }}
        sx={{ p: 2 }}
      >
        <DialogContent>
          <Box>
            <Typography variant={'h3'} sx={{ mb: 3 }}>Change Password</Typography>

            <Typography variant={'body1'} sx={{ mb: 3 }}>
              Please provide your email address and click the button below. We&apos;ll send you an email with a link to reset your password. You&apos;ll be logged out of your active sessions.
            </Typography>

            <Box sx={{ width: '100%' }}>
              <Formik
                initialValues={defaultForm}
                onSubmit={(values) => {
                  handleRequestPasswordReset(values)
                }}
              >
                {formikProps => (
                  <form
                    autoComplete={'off'}
                    autoCorrect={'off'}
                    onSubmit={formikProps.handleSubmit}
                  >
                    <Grid container spacing={2}>
                      {!(formikProps.isValid) && (
                        <Grid item md={12} sx={{ mt: 3 }}>
                          <ErrorComponent title={'Form validation error'} message={Object.values(formikProps.errors).join(', ')}/>
                        </Grid>
                      )}

                      <Grid item md={12} xs={12}>
                        <TextField
                          label={'Email Address'}
                          type={'email'}
                          variant="outlined"
                          fullWidth={true}
                          placeholder={'Email Address'}
                          {...formikProps.getFieldProps('username')}
                        />
                      </Grid>
                      <Grid item md={12} xs={12}>
                        <Button
                          variant={'contained'}
                          type="submit"
                          sx={{ mr: 2 }}
                          disabled={requestPasswordResetApiResult.isLoading}>
                          Send
                          {(requestPasswordResetApiResult.isLoading && <ButtonLoadingComponent/>)}
                        </Button>
                      </Grid>

                    </Grid>
                  </form>
                )}
              </Formik>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default ForgotPasswordComponent
